/**
 * PageTurnTestPage
 * Visuel verifikation af magasin page-turn overgangen.
 * Tilgængelig på /page-turn-test — ingen auth nødvendig.
 *
 * Viser to på hinanden følgende billeder med page-turn effekten
 * der er implementeret i ReplayMap's foto-display system.
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';

// Test-billeder (lokale assets der altid er tilgængelige)
const PHOTOS = [
  { id: 'p1', url: '/assets/bg_alpine.jpg',   caption: 'Alper — side 1 af 3' },
  { id: 'p2', url: '/assets/mc_side_view.png', caption: 'Motorcykel — side 2 af 3' },
  { id: 'p3', url: '/assets/bg_alpine.jpg',   caption: 'Alper igen — side 3 af 3' },
];

export default function PageTurnTestPage() {
  const frontRef     = useRef(null);
  const backImgRef   = useRef(null);
  const frontImgRef  = useRef(null);
  const inProgress   = useRef(false);
  const [idx, setIdx]        = useState(0);
  const [autoPlay, setAuto]  = useState(false);
  const autoRef              = useRef(false);

  /* ── Page-turn kerne ─────────────────────────────────────────────── */
  const turnTo = useCallback((nextIdx) => {
    const front   = frontRef.current;
    const backImg = backImgRef.current;
    const frontImg = frontImgRef.current;
    if (!front || !backImg || !frontImg || inProgress.current) return;

    const nextPhoto = PHOTOS[nextIdx];
    inProgress.current = true;

    // 1. Sæt næste billede i baglag
    backImg.src           = nextPhoto.url;
    backImg.style.display = 'block';

    // 2. Fold forlaget væk (CSS-class trigger)
    front.classList.add('page-turn-folding');

    // 3. Halvvejs: swap forlaget usynligt (det er edge-on = 0px bred)
    setTimeout(() => {
      frontImg.src = nextPhoto.url;

      // Reset front instant (usynlig, ingen flash)
      front.style.transition = 'none';
      front.classList.remove('page-turn-folding');

      // Skjul baglag
      backImg.style.display = 'none';
      backImg.src = '';

      // Genindstil transition næste frame
      requestAnimationFrame(() => requestAnimationFrame(() => {
        front.style.transition = '';
        inProgress.current = false;
        setIdx(nextIdx);
      }));
    }, 680);
  }, []);

  /* ── Auto-play ───────────────────────────────────────────────────── */
  useEffect(() => {
    autoRef.current = autoPlay;
  }, [autoPlay]);

  useEffect(() => {
    if (!autoPlay) return;
    const id = setInterval(() => {
      if (!autoRef.current) return;
      setIdx(prev => {
        const next = (prev + 1) % PHOTOS.length;
        turnTo(next);
        return prev; // Opdateres inde i turnTo via setIdx
      });
    }, 2200);
    return () => clearInterval(id);
  }, [autoPlay, turnTo]);

  const handlePrev = () => turnTo((idx - 1 + PHOTOS.length) % PHOTOS.length);
  const handleNext = () => turnTo((idx + 1) % PHOTOS.length);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#060e0a',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 32,
      padding: 24,
      fontFamily: 'system-ui, sans-serif',
      color: '#fff',
    }}>
      <h1 style={{ fontSize: 18, color: '#2DFF88', margin: 0, letterSpacing: 3, textTransform: 'uppercase' }}>
        Magasin Page-Turn Test
      </h1>

      {/* ── Scene ── */}
      <div style={{ position: 'relative', width: 320, height: 440, perspective: '1600px' }}>

        {/* Baglag — næste billede */}
        <div style={{
          position: 'absolute', inset: 0,
          borderRadius: 22, overflow: 'hidden',
          background: '#0c1611', zIndex: 1,
          boxShadow: '0 16px 40px rgba(0,0,0,0.8)',
        }}>
          <img
            ref={backImgRef}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'none' }}
          />
        </div>

        {/* Forlag — nuværende billede */}
        <div
          ref={frontRef}
          style={{
            position: 'absolute', inset: 0,
            borderRadius: 22, overflow: 'hidden',
            background: '#0c1611', zIndex: 2,
            boxShadow: '0 24px 56px rgba(0,0,0,0.9), 0 0 0 1.5px rgba(45,255,136,0.35)',
            transformOrigin: 'left center',
          }}
          className="page-turn-demo-front"
        >
          <img
            ref={frontImgRef}
            alt=""
            src={PHOTOS[0].url}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          {/* Caption */}
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
            padding: '40px 20px 20px',
            fontSize: 13, color: 'rgba(255,255,255,0.85)', letterSpacing: 1,
          }}>
            {PHOTOS[idx].caption}
          </div>
        </div>
      </div>

      {/* ── Kontrolbar ── */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <button
          onClick={handlePrev}
          style={btnStyle}
        >← Forrige</button>

        <button
          onClick={() => { setAuto(v => !v); }}
          style={{ ...btnStyle, background: autoPlay ? 'rgba(45,255,136,0.25)' : 'rgba(255,255,255,0.08)' }}
        >{autoPlay ? '⏸ Stop' : '▶ Auto (2.2s)'}</button>

        <button
          onClick={handleNext}
          style={btnStyle}
        >Næste →</button>
      </div>

      <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', textAlign: 'center', maxWidth: 280 }}>
        Klik "Næste" for manuelt sideskift, eller tryk "Auto" for automatisk afspilning.
        Effekten er identisk med den der sker i det rigtige replay.
      </p>

      {/* CSS for page-turn animation */}
      <style>{`
        @keyframes pageTurnFold {
          0%   { transform: perspective(1600px) rotateY(0deg); }
          14%  { transform: perspective(1600px) rotateY(-14deg); }
          52%  { transform: perspective(1600px) rotateY(-62deg); }
          86%  { transform: perspective(1600px) rotateY(-85deg); }
          100% { transform: perspective(1600px) rotateY(-90deg) scale(0.97); }
        }
        @keyframes pageCurlShadow {
          0%   { opacity: 0; transform: translateX(110%); }
          14%  { opacity: 1; }
          72%  { opacity: 0.85; }
          100% { opacity: 0.08; transform: translateX(-18%); }
        }
        .page-turn-demo-front.page-turn-folding {
          transform-origin: left center !important;
          animation: pageTurnFold 680ms cubic-bezier(0.25, 0.10, 0.15, 1.0) forwards !important;
          box-shadow: 0 8px 16px rgba(0,0,0,0.4) !important;
          will-change: transform;
        }
        .page-turn-demo-front.page-turn-folding::after {
          content: '';
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 10;
          background: linear-gradient(to right,
            rgba(0,0,0,0.00) 0%,
            rgba(0,0,0,0.20) 18%,
            rgba(0,0,0,0.72) 52%,
            rgba(0,0,0,0.95) 74%,
            rgba(0,0,0,0.55) 100%
          );
          animation: pageCurlShadow 680ms cubic-bezier(0.25, 0.10, 0.15, 1.0) forwards;
        }
      `}</style>
    </div>
  );
}

const btnStyle = {
  padding: '10px 20px',
  background: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: 10,
  color: '#fff',
  fontSize: 13,
  cursor: 'pointer',
  letterSpacing: 0.5,
};
